import { defineMessages } from 'react-intl';

/**
 * Medical-device compliance copy for the final onboarding step (RCO-21 /
 * SWR-188): the pointer to the Instructions for Use, the in-consultation
 * reminders, the pre-consult mic check, and the not-a-medical-device statement
 * the EU/EEA sees in place of the first two.
 *
 * IDs are minted by the `formatjs/enforce-id` autofix — run
 * `pnpm exec eslint --fix` on this file rather than hand-writing them.
 */
export const privacyStepMessages = defineMessages({
  ifuTitle: {
    defaultMessage: 'Read your instructions for Use',
    id: '3q/+ZqhUvJ',
    description: 'Heading of the Instructions for Use block on the final onboarding step',
  },
  ifuDescription: {
    defaultMessage:
      'They cover intended use, limitations and residual risks. Available any time from {helpIcon} Help in Heidi Scribe and Evidence.',
    id: 'iRVybIVL14',
    description:
      'Body of the Instructions for Use block. {helpIcon} renders the in-app Help question-mark icon inline',
  },
  remindersTitle: {
    defaultMessage: 'Reminders for using Heidi in a consultation',
    id: 'fV2V+BWZDn',
    description:
      'Trigger label of the in-consultation reminders accordion on the final onboarding step',
  },
  reminderConsentTitle: {
    defaultMessage: 'Consent',
    id: '52m+kG7kJm',
    description: 'Subheading of the consent reminder',
  },
  reminderConsentBody: {
    defaultMessage:
      'Ask the patient for consent to record before starting the session. Explain that Heidi transcribes the consultation and produces a draft note, and that you review the note before it is used.',
    id: '/OY2T/wlml',
    description: 'Body of the consent reminder',
  },
  reminderAmbiguousTitle: {
    defaultMessage: 'Ambiguous terms',
    id: 'a+Esvd++FP',
    description: 'Subheading of the ambiguous-terms reminder',
  },
  reminderAmbiguousBody: {
    defaultMessage:
      'Correct anything unclear or contradictory out loud during the consultation. Repeat medication names, doses and numbers. Heidi transcribes what it hears, so ambiguous speech produces an ambiguous note.',
    id: 'Se0hAZno2f',
    description: 'Body of the ambiguous-terms reminder',
  },
  reminderNonVerbalTitle: {
    defaultMessage: 'Non-verbal cues',
    id: 'glFHAE5WOE',
    description: 'Subheading of the non-verbal cues reminder',
  },
  reminderNonVerbalBody: {
    defaultMessage:
      'Heidi captures only what is spoken. State any non-verbal response aloud, for example "the patient is nodding to confirm consent to record", or "the patient is indicating their lower left abdomen".',
    id: 'wy+TL+LWQ/',
    description: 'Body of the non-verbal cues reminder',
  },
  reminderRetentionTitle: {
    defaultMessage: 'Recording retention',
    id: 'fggrVSTLKg',
    description: 'Subheading of the recording-retention reminder',
  },
  reminderRetentionBody: {
    defaultMessage:
      'Audio is not retained after the note is generated. No recording is stored post consultation, and conversations are not used to train AI.',
    id: 'a2dKyKf3jE',
    description: 'Body of the recording-retention reminder',
  },
  reminderReviewTitle: {
    defaultMessage: 'Reviewing output',
    id: 'O9LyJp9peU',
    description: 'Subheading of the output-review reminder',
  },
  reminderReviewBody: {
    defaultMessage:
      'Review every note before use. Heidi produces a draft, and the clinician is responsible for what enters the medical record. Edit anything that is incorrect, missing or ambiguous.',
    id: 'JgcfgXH6T+',
    description: 'Body of the output-review reminder',
  },
  remindersFooter: {
    defaultMessage:
      "If something isn't working or you're unsure how to use a feature, get in touch.",
    id: 'rguRZbhUl7',
    description: 'Closing line of the in-consultation reminders accordion',
  },
  micTipTitle: {
    defaultMessage: 'Before your consult',
    id: 'CrcVgGfr9f',
    description: 'Heading of the pre-consult microphone check card',
  },
  micTipBody: {
    defaultMessage:
      'Check your mic in the top right corner is working. It should show dynamic waveforms, or simply try it by making a test note.',
    id: 'sc3SlBtT0k',
    description: 'Body of the pre-consult microphone check card',
  },
  notMedicalDeviceBody: {
    defaultMessage:
      'Heidi Scribe is a documentation admin tool for healthcare professionals. With consent, it transcribes your consultation and drafts a note. You review, correct and approve every draft, and you remain the author of the record. It is not a medical device and is not intended to be used for any medical purpose.',
    id: 'TdA505T9z8',
    description:
      'Body of the not-a-medical-device callout shown to EU/EEA users on the final onboarding step. Regulator-facing wording — do not reword or trim without compliance sign-off.',
  },
});
